/* eslint-disable no-console */
import type { AxiosError, AxiosInstance } from 'axios';
import axios from 'axios';
import debounce from 'lodash.debounce';

import { createExponentialRetryStrategy, withRetries } from '@genially/commons';

import type { SendLogRequest } from '../types/SendLogRequest';
import type { Logger, LogMetadata } from './Logger';

const MaxBodySize = 246 * 1024;

export class RemoteLogger implements Logger {
  private readonly app: string;

  private client: AxiosInstance;

  private readonly messages: Array<{ req: SendLogRequest; size: number }> = [];

  private sending = false;

  private defaultMetadata: LogMetadata;

  constructor({ app, url, origin }: { app: string; url: string; origin?: string }) {
    this.app = app;
    this.defaultMetadata = {};

    const headers: Record<string, string> = {};

    if (origin !== undefined) {
      headers.origin = origin;
    }

    this.client = axios.create({
      baseURL: url,
      headers,
    });
  }

  private send = debounce(
    () => {
      if (this.messages.length === 0) return;

      if (this.sending) {
        setTimeout(this.send.bind(this), 5000);
        return;
      }

      this.sending = true;

      const requests: SendLogRequest[] = [];

      let requestsSize = 0;
      const numMessages = this.messages.length;
      for (let i = 0; i < numMessages; i += 1) {
        if (requestsSize + this.messages[0].size > MaxBodySize) {
          setTimeout(this.send.bind(this), 5000);
          break;
        }

        const message = this.messages.shift();

        if (!message) {
          break;
        }

        requests.push(message.req);
        requestsSize += message.size;
      }

      withRetries(
        () => this.client.post('/log', requests),
        createExponentialRetryStrategy({
          step: 2000,
          max: 16000,
          multiplier: 2,
          shouldRetryError: (err: AxiosError): boolean => {
            if (!err.isAxiosError) {
              return true;
            }

            const status = err?.response?.status;
            const isHandledError = status !== undefined && status >= 400 && status < 500;

            // Just in case the network is down or WAF is blocking the request
            const isNetworkError = !err.response;

            if (isHandledError || isNetworkError) {
              console.error(
                '[Logger] Cannot deliver the message to logger server due unexpected status code',
                err,
              );

              // enqueue back the messages that could not be sent
              this.messages.unshift(
                ...requests.map(req => ({ req, size: JSON.stringify(req).length })),
              );

              return false;
            }

            return true;
          },
        }),
      ).finally(() => {
        this.sending = false;
      });
    },
    5000,
    { maxWait: 30000 },
  );

  private sendLog(
    level: SendLogRequest['level'],
    message: string,
    metadata: LogMetadata | undefined,
  ): void {
    const request: SendLogRequest = {
      app: this.app,
      level,
      message,
      metadata,
      timestamp: Date.now(),
    };

    const size = JSON.stringify(request).length;

    if (size > MaxBodySize) {
      console.warn(
        `[Logger] The message is too big to be sent to the logger server. Size: ${size} bytes`,
        { request },
      );

      return;
    }

    this.messages.push({
      req: request,
      size,
    });

    this.send();
  }

  debug(message: string, metadata?: LogMetadata): void {
    this.sendLog('debug', message, { ...this.defaultMetadata, ...metadata });
  }

  error(message: string, metadata?: LogMetadata): void {
    this.sendLog('error', message, { ...this.defaultMetadata, ...metadata });
  }

  info(message: string, metadata?: LogMetadata): void {
    this.sendLog('info', message, { ...this.defaultMetadata, ...metadata });
  }

  warn(message: string, metadata?: LogMetadata): void {
    this.sendLog('warn', message, { ...this.defaultMetadata, ...metadata });
  }

  setDefaultMetadata(metadata: LogMetadata): void {
    this.defaultMetadata = metadata;
  }
}

export const createRemoteLogger = ({
  app,
  remoteHost,
}: {
  app: string;
  remoteHost: string;
}): RemoteLogger => {
  return new RemoteLogger({ app, url: remoteHost });
};
